import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";

interface ConfigDialogProps {
  isOpen: boolean;
  onClose: () => void;
  config: {
    stockLength: number;
    bladeWidth: number;
    minCutLength: number;
    useRemnants: boolean;
  };
  onUpdateConfig: (config: any) => void;
  onResetConfig: () => void;
}

const ConfigDialog: React.FC<ConfigDialogProps> = ({
  isOpen,
  onClose,
  config,
  onUpdateConfig,
  onResetConfig,
}) => {
  const [stockLength, setStockLength] = useState(config.stockLength.toString());
  const [bladeWidth, setBladeWidth] = useState(config.bladeWidth.toString());
  const [minCutLength, setMinCutLength] = useState(config.minCutLength.toString());
  const [useRemnants, setUseRemnants] = useState(config.useRemnants);

  // Update local state when config changes
  useEffect(() => {
    setStockLength(config.stockLength.toString());
    setBladeWidth(config.bladeWidth.toString());
    setMinCutLength(config.minCutLength.toString());
    setUseRemnants(config.useRemnants);
  }, [config, isOpen]);

  const handleSave = () => {
    onUpdateConfig({
      stockLength: parseFloat(stockLength) || 290,
      bladeWidth: parseFloat(bladeWidth) || 0.125,
      minCutLength: parseFloat(minCutLength) || 6,
      useRemnants,
    });
    onClose();
  };

  const handleReset = () => {
    onResetConfig();
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Cut Optimizer Settings</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="grid gap-4">
            <div className="grid gap-2">
              <Label htmlFor="stock-length">Stock Length (inches)</Label>
              <Input
                id="stock-length"
                type="number"
                step="1"
                min="1"
                value={stockLength}
                onChange={(e) => setStockLength(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">Standard length of stock material</p>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="blade-width">Blade Width (inches)</Label>
              <Input
                id="blade-width"
                type="number"
                step="0.001"
                min="0.001"
                value={bladeWidth}
                onChange={(e) => setBladeWidth(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">Width of the cutting blade (kerf)</p>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="min-cut-length">Minimum Cut Length (inches)</Label>
              <Input
                id="min-cut-length"
                type="number"
                step="0.125"
                min="0.125"
                value={minCutLength}
                onChange={(e) => setMinCutLength(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">Minimum length for drops</p>
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="use-remnants">Use Drops</Label>
                <p className="text-xs text-muted-foreground">Reuse leftover material when possible</p>
              </div>
              <Switch
                id="use-remnants"
                checked={useRemnants}
                onCheckedChange={setUseRemnants}
              />
            </div>
          </div>
        </div>

        <DialogFooter className="flex justify-between">
          <Button type="button" variant="outline" onClick={handleReset}>
            Reset to Defaults
          </Button>
          <div className="space-x-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="button" onClick={handleSave}>
              Save Settings
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ConfigDialog;
